import { denyClarityConsent, grantClarityConsent } from '@core/clarity';
import { GOOGLE_ANALYTICS_CONTAINER } from '@core/config';

const GA_MEASUREMENT_ID = GOOGLE_ANALYTICS_CONTAINER || 'G-P3HCDPM475';
const GA_SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
  GA_MEASUREMENT_ID,
)}`;
const CONSENT_COOKIE_NAME = 'cookieConsent';
const CONSENT_STORAGE_KEY = 'pnk.analytics.consent';
const PAGEVIEW_DEDUPE_WINDOW_MS = 1200;

type ConsentDecision = 'unknown' | 'accepted' | 'rejected';
type ConsentMode = 'granted' | 'denied';
type EventPolicy = 'always' | 'granted_only';

type EventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __PRERENDER__?: boolean;
  }
}

let initialized = false;
let activeConsentMode: ConsentMode = 'denied';
let lastPageViewKey = '';
let lastPageViewAt = 0;

const consentDeniedPayload = {
  analytics_storage: 'denied',
};

const consentGrantedPayload = {
  analytics_storage: 'granted',
};

const isBrowser = (): boolean => typeof window !== 'undefined';

export const isPrerenderFlow = (): boolean => isBrowser() && window.__PRERENDER__ === true;

const ensureDataLayer = () => {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
};

const ensureGtag = () => {
  if (!isBrowser()) return;
  ensureDataLayer();
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
};

const gtag = (...args: unknown[]) => {
  if (!isBrowser() || isPrerenderFlow()) return;
  ensureGtag();
  window.gtag?.(...args);
};

const loadGtagScript = () => {
  if (!isBrowser() || isPrerenderFlow()) return;
  const existing = document.querySelector(
    `script[src^="https://www.googletagmanager.com/gtag/js?id="]`,
  );
  if (existing) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = GA_SCRIPT_SRC;
  document.head.appendChild(script);
};

const readCookie = (name: string): string | null => {
  if (!isBrowser()) return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name: string, value: string, days = 150) => {
  if (!isBrowser()) return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value,
  )}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

const readStoredConsent = (): ConsentDecision => {
  if (!isBrowser()) return 'unknown';
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw === 'accepted' || raw === 'rejected') return raw;
  } catch {
    // ignore storage errors
  }
  return 'unknown';
};

const storeConsent = (decision: Exclude<ConsentDecision, 'unknown'>) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, decision);
  } catch {
    // ignore storage errors
  }
};

export const getConsentDecision = (): ConsentDecision => {
  const stored = readStoredConsent();
  if (stored !== 'unknown') return stored;
  const raw = readCookie(CONSENT_COOKIE_NAME);
  if (raw == null || raw === '') return 'unknown';
  if (raw === 'true' || raw === 'accepted') return 'accepted';
  if (raw === 'false' || raw === 'rejected') return 'rejected';
  return 'unknown';
};

const applyConsentMode = (mode: ConsentMode) => {
  activeConsentMode = mode;
  gtag('consent', 'update', mode === 'granted' ? consentGrantedPayload : consentDeniedPayload);
};

export const initAnalytics = () => {
  if (!isBrowser() || isPrerenderFlow() || initialized) return;
  initialized = true;

  ensureGtag();
  loadGtagScript();
  gtag('consent', 'default', consentDeniedPayload);
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const decision = getConsentDecision();
  if (decision === 'accepted') {
    applyConsentMode('granted');
    grantClarityConsent();
  } else {
    applyConsentMode('denied');
    denyClarityConsent();
  }
};

const canSendEvent = (policy: EventPolicy) =>
  policy === 'always' || activeConsentMode === 'granted';

export const trackEvent = (
  eventName: string,
  params: EventParams = {},
  policy: EventPolicy = 'granted_only',
) => {
  if (!isBrowser() || isPrerenderFlow()) return;
  if (!initialized) initAnalytics();
  if (!canSendEvent(policy)) return;
  gtag('event', eventName, params);
};

export const trackPageView = (
  locationLike: { pathname: string; search?: string },
  referrer?: string,
) => {
  if (!isBrowser() || isPrerenderFlow()) return;
  if (!initialized) initAnalytics();

  const pagePath = `${locationLike.pathname}${locationLike.search || ''}`;
  const now = Date.now();
  if (lastPageViewKey === pagePath && now - lastPageViewAt < PAGEVIEW_DEDUPE_WINDOW_MS) {
    return;
  }
  lastPageViewKey = pagePath;
  lastPageViewAt = now;

  const pageLocation = `${window.location.origin}${pagePath}`;
  const pageTitle = document.title;
  const pageReferrer = referrer || document.referrer || undefined;

  gtag('event', 'page_view', {
    page_path: pagePath,
    page_location: pageLocation,
    page_title: pageTitle,
    page_referrer: pageReferrer,
  });
};

export const acceptAnalyticsConsent = () => {
  if (!isBrowser() || isPrerenderFlow()) return;
  storeConsent('accepted');
  writeCookie(CONSENT_COOKIE_NAME, 'accepted');
  applyConsentMode('granted');
  trackEvent('consent_accept', {}, 'always');
  grantClarityConsent();
};

export const rejectAnalyticsConsent = () => {
  if (!isBrowser() || isPrerenderFlow()) return;
  storeConsent('rejected');
  writeCookie(CONSENT_COOKIE_NAME, 'rejected');
  applyConsentMode('denied');
  trackEvent('consent_reject', {}, 'always');
  denyClarityConsent();
};

export const getActiveConsentMode = (): ConsentMode => activeConsentMode;
